import { useState } from "react";

const BILL_EMAIL = "bcaskey@caskeytraining.com";

const QUESTIONS = [
  { id: "role", label: "What best describes your role?", type: "select", options: ["Business Owner / Founder", "VP of Sales", "Sales Manager", "Individual Sales Rep", "Consultant / Coach"] },
  { id: "revenue_now", label: "Where is your revenue right now?", type: "select", options: ["Under $250K/yr", "$250K–$500K/yr", "$500K–$1M/yr", "$1M–$3M/yr", "$3M+/yr"] },
  { id: "revenue_target", label: "What's your 90-day revenue target?", type: "select", options: ["Add $25K–$50K", "Add $50K–$100K", "Add $100K–$250K", "Add $250K+", "Stabilize what I have first"] },
  {
    id: "biggest_bottleneck",
    label: "Where are you stuck right now? Pick your top 1–3.",
    type: "multiselect", max: 3,
    options: ["Not enough prospects in the pipeline", "Prospects go cold before they buy", "Low close rates / losing deals", "Positioning — I don't stand out", "My team isn't performing", "I'm doing everything myself"]
  },
  { id: "tried_already", label: "What have you already tried that hasn't worked?", type: "select", options: ["More cold outreach / cold calling", "Sales training programs", "Hiring more salespeople", "Better CRM / tech tools", "Social media / content creation", "Nothing structured — I've been winging it"] },
  {
    id: "personal_obstacle",
    label: "What's holding you back personally? Pick your top 1–3.",
    type: "multiselect", max: 3,
    options: ["Mindset — fear, doubt, or hesitation", "Time — I'm stretched too thin", "Skills — I don't know what I don't know", "Consistency — I start strong but lose momentum", "Team — the people around me aren't bought in", "Confidence in my own value / offer"]
  },
  { id: "strengths", label: "Where do you feel strongest right now?", type: "select", options: ["Building relationships", "Subject matter expertise", "Closing when I'm in front of the right person", "Content / storytelling", "Systems and process", "Leadership and vision"] },
  { id: "commitment", label: "How many hours per week can you realistically commit?", type: "select", options: ["2–3 hours/week", "4–6 hours/week", "7–10 hours/week", "10+ hours/week"] },
  { id: "name", label: "What's your first name?", type: "text", placeholder: "e.g. Sarah" },
  { id: "email", label: "What's your email address?", type: "text", placeholder: "e.g. sarah@company.com" },
  { id: "one_win", label: "Complete this: 90 days from now, I'll know this worked if...", type: "text", placeholder: "e.g. I have 3 new clients and a full pipeline for Q3..." },
];

const SYSTEM_PROMPT = `You are Bill Caskey, founder of Caskey Training. You have 30+ years training B2B sales organizations. A new member just joined your 2X Sprint — a 90-day revenue acceleration program. Based on their intake answers, create their personalized 90-Day Revenue Roadmap.

YOUR VOICE: Direct, philosophical, warm but no-nonsense. You challenge conventional thinking. You believe in the person's higher purpose and their ability to achieve extraordinary results. You are their Guide, not their boss.

CORE PHILOSOPHIES — weave these in throughout the roadmap:
- You are a Guide, not a convincer. Help them see their reality clearly.
- Detachment from outcomes — desperation repels. Focus on inputs, not results.
- Qualification over closing — a 50%+ close rate is the standard. If it's lower, the problem is upstream.
- Leverage over labor — assets (knowledge, relationships, systems) beat raw effort every time.
- Inputs greater than outputs — master your daily disciplines and the results take care of themselves.
- The Sales Engine — systematic, repeatable revenue always beats heroics and hope.
- Prospects rarely know their real problem. The stated issue is almost always a symptom. Diagnose deeper.

DIAGNOSTIC LOGIC — this is critical. When someone reports a bottleneck, do NOT take it at face value. Diagnose the ROOT CAUSE and address that in the roadmap:

BOTTLENECK: "Low close rates / losing deals"
→ Never recommend closing techniques. The real cause is almost always one of:
  a) They are unclear on their own offer — they cannot articulate the value precisely
  b) They don't know how to communicate the offer in a way that lands with the buyer
  c) They have no visual roadmap or process to share with the prospect — so the buyer has no map to follow and stalls
→ Address which root cause fits best given their other answers. Week 1 should begin diagnosing and clarifying their offer.

BOTTLENECK: "Not enough prospects in the pipeline"
→ This is rarely an activity problem. The real cause is almost always:
  a) Asset blindness — they have relationships, knowledge, and content they aren't leveraging
  b) Positioning problem — they aren't attracting because they haven't defined who they serve and what problem they solve
  c) No lead generation system — they rely on referrals and hope instead of a repeatable engine
→ Never tell them to make more calls. Diagnose which asset they're sitting on and ignoring.

BOTTLENECK: "Prospects go cold before they buy"
→ The real cause is almost always:
  a) No follow-up system — they rely on memory and good intentions
  b) Proposals sent too early — before the prospect has admitted the cost of their problem
  c) The prospect was never truly qualified — they were comfortable conversations, not real opportunities
→ Address the qualification process and follow-up engine, not persistence techniques.

BOTTLENECK: "Positioning — I don't stand out"
→ The real cause is almost always:
  a) No defined niche — they try to serve everyone and end up invisible
  b) They lead with what they do instead of the problem they solve
  c) No Guide framework — they show up as a vendor, not a trusted expert
→ Week 1 should focus on clarifying their unique value and the specific problem they solve.

BOTTLENECK: "My team isn't performing"
→ The real cause is almost always:
  a) The leader isn't modeling the behavior they expect
  b) No accountability structure — people have targets but no plan to hit them
  c) Wrong people in wrong roles — or people who are plateaued and don't know it
→ Address leadership inputs first. What is the leader doing or not doing that's creating this?

BOTTLENECK: "I'm doing everything myself"
→ The real cause is almost always:
  a) No systems — everything lives in their head, so they can't delegate
  b) Asset blindness — they haven't built repeatable processes from their own expertise
  c) Leverage deficit — they are in labor mode, not leverage mode
→ The first month should be about identifying what can be systematized or delegated.

PERSONAL OBSTACLE LOGIC:
- Mindset + Consistency = identity issue. They don't yet see themselves as the person who does this work.
- Time + Skills = prioritization issue. They are busy doing the wrong things.
- Team + Mindset = leadership confidence gap. They don't trust themselves to lead decisively.
- Any combination including "Confidence in my own value / offer" = the offer itself needs to be clarified before anything else.

COMBINATION LOGIC — when someone selects multiple bottlenecks, look for the underlying theme:
- Pipeline + Positioning = they haven't defined who they serve, so they can't find them
- Close rates + Prospects going cold = the qualification process is broken at the front end
- Team + Doing everything myself = they haven't built a leverage system or trained to their own standards
- Multiple bottlenecks = always sequence: fix foundation first (mindset/clarity), then system (pipeline/process), then execution (closing/team)

CRITICAL: Respond ONLY with a valid JSON object. No preamble, no markdown, no explanation — raw JSON only.

{
  "headline": "Bold personalized 1-line headline naming their specific combination of challenges",
  "opening": "2-3 sentences speaking directly to them. Reference their specific bottleneck combination. Name the real issue underneath what they reported. Be direct and specific.",
  "month1": {
    "theme": "Month 1 theme title",
    "focus": "1 sentence on what this month is really about — always foundation and diagnosis first",
    "weeks": [
      {"week": "Week 1", "priority": "Short action-oriented priority", "why": "1 sentence on why this matters first"},
      {"week": "Week 2", "priority": "Short action-oriented priority", "why": "1 sentence on why"},
      {"week": "Week 3", "priority": "Short action-oriented priority", "why": "1 sentence on why"},
      {"week": "Week 4", "priority": "Short action-oriented priority", "why": "1 sentence on why"}
    ],
    "caskey_call": "One direct coaching challenge from Bill — provocative, specific, not generic"
  },
  "month2": {
    "theme": "Month 2 theme title",
    "focus": "1 sentence — this is where systems get built",
    "weeks": [
      {"week": "Week 5", "priority": "priority", "why": "why"},
      {"week": "Week 6", "priority": "priority", "why": "why"},
      {"week": "Week 7", "priority": "priority", "why": "why"},
      {"week": "Week 8", "priority": "priority", "why": "why"}
    ],
    "caskey_call": "One direct coaching challenge from Bill for this month"
  },
  "month3": {
    "theme": "Month 3 theme title",
    "focus": "1 sentence — this is where execution and results compound",
    "weeks": [
      {"week": "Week 9", "priority": "priority", "why": "why"},
      {"week": "Week 10", "priority": "priority", "why": "why"},
      {"week": "Week 11", "priority": "priority", "why": "why"},
      {"week": "Week 12", "priority": "priority", "why": "why"}
    ],
    "caskey_call": "One direct coaching challenge from Bill for this month"
  },
  "north_star": "Their single most important metric or milestone for the full 90 days — make it specific and measurable",
  "bill_closing": "2-3 sentences from Bill — personal, direct, references their specific situation. End with UPWARD."
}`;

function buildPrompt(answers) {
  const bottlenecks = Array.isArray(answers.biggest_bottleneck) ? answers.biggest_bottleneck.join(", ") : answers.biggest_bottleneck;
  const obstacles = Array.isArray(answers.personal_obstacle) ? answers.personal_obstacle.join(", ") : answers.personal_obstacle;
  return `${SYSTEM_PROMPT}

New 2X Sprint member intake:
Name: ${answers.name}
Role: ${answers.role}
Current Revenue: ${answers.revenue_now}
90-Day Target: ${answers.revenue_target}
Where They're Stuck (multiple): ${bottlenecks}
What They've Tried: ${answers.tried_already}
Personal Obstacles (multiple): ${obstacles}
Current Strength: ${answers.strengths}
Weekly Time Commitment: ${answers.commitment}
Definition of Success: ${answers.one_win}

Study the combination of bottlenecks and obstacles carefully. Build their personalized 90-Day Revenue Roadmap now. Return ONLY valid JSON.`;
}

function buildBuyerEmail(name, roadmap, answers) {
  return `Hi ${name},

Welcome to the 2X Sprint. Your personalized 90-Day Revenue Roadmap is ready.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${roadmap.headline}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${roadmap.opening}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
YOUR 90-DAY NORTH STAR
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${roadmap.north_star}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
MONTH 1 — ${roadmap.month1.theme.toUpperCase()}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${roadmap.month1.focus}

${roadmap.month1.weeks.map(w => `${w.week}: ${w.priority}\n-> ${w.why}`).join("\n\n")}

Bill's Call: "${roadmap.month1.caskey_call}"

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
MONTH 2 — ${roadmap.month2.theme.toUpperCase()}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${roadmap.month2.focus}

${roadmap.month2.weeks.map(w => `${w.week}: ${w.priority}\n-> ${w.why}`).join("\n\n")}

Bill's Call: "${roadmap.month2.caskey_call}"

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
MONTH 3 — ${roadmap.month3.theme.toUpperCase()}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${roadmap.month3.focus}

${roadmap.month3.weeks.map(w => `${w.week}: ${w.priority}\n-> ${w.why}`).join("\n\n")}

Bill's Call: "${roadmap.month3.caskey_call}"

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
FROM BILL
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${roadmap.bill_closing}

— Bill Caskey
billcaskey.com`;
}

function buildBillEmail(name, email, roadmap, answers) {
  const bottlenecks = Array.isArray(answers.biggest_bottleneck) ? answers.biggest_bottleneck.join(", ") : answers.biggest_bottleneck;
  const obstacles = Array.isArray(answers.personal_obstacle) ? answers.personal_obstacle.join(", ") : answers.personal_obstacle;
  return `NEW 2X SPRINT MEMBER
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Name: ${name}
Email: ${email}
Role: ${answers.role}
Current Revenue: ${answers.revenue_now}
90-Day Target: ${answers.revenue_target}
Bottlenecks: ${bottlenecks}
Tried Already: ${answers.tried_already}
Personal Obstacles: ${obstacles}
Strength: ${answers.strengths}
Time/Week: ${answers.commitment}
Definition of Win: ${answers.one_win}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
GENERATED ROADMAP
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${roadmap.headline}

North Star: ${roadmap.north_star}

MONTH 1 — ${roadmap.month1.theme}
${roadmap.month1.weeks.map(w => `${w.week}: ${w.priority}`).join("\n")}
Bill's Call: "${roadmap.month1.caskey_call}"

MONTH 2 — ${roadmap.month2.theme}
${roadmap.month2.weeks.map(w => `${w.week}: ${w.priority}`).join("\n")}
Bill's Call: "${roadmap.month2.caskey_call}"

MONTH 3 — ${roadmap.month3.theme}
${roadmap.month3.weeks.map(w => `${w.week}: ${w.priority}`).join("\n")}
Bill's Call: "${roadmap.month3.caskey_call}"

Closing: ${roadmap.bill_closing}`;
}

function ProgressBar({ current, total }) {
  const pct = Math.round((current / total) * 100);
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ color: "#C9A84C", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", fontFamily: "Georgia, serif" }}>Question {current} of {total}</span>
        <span style={{ color: "#C9A84C", fontSize: 11, fontFamily: "Georgia, serif" }}>{pct}%</span>
      </div>
      <div style={{ height: 2, background: "#1A2E45", borderRadius: 2 }}>
        <div style={{ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg, #C9A84C, #E8C97A)", borderRadius: 2, transition: "width 0.5s ease" }} />
      </div>
    </div>
  );
}

function MultiSelect({ options, selected, onChange, max }) {
  const toggle = (opt) => {
    if (selected.includes(opt)) onChange(selected.filter(s => s !== opt));
    else if (selected.length < max) onChange([...selected, opt]);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9, marginBottom: 28 }}>
      <div style={{ color: "#64748b", fontSize: 11, fontFamily: "Georgia, serif", marginBottom: 4, letterSpacing: 1 }}>
        Select up to {max} — {selected.length} chosen
      </div>
      {options.map((opt) => {
        const isSelected = selected.includes(opt);
        const isDisabled = !isSelected && selected.length >= max;
        return (
          <button key={opt} onClick={() => toggle(opt)} disabled={isDisabled}
            style={{ background: isSelected ? "#C9A84C18" : "#060D16", border: isSelected ? "1px solid #C9A84C" : "1px solid #1A2E45", borderRadius: 8, padding: "11px 16px", color: isSelected ? "#C9A84C" : isDisabled ? "#334155" : "#94a3b8", fontSize: 13, fontFamily: "Georgia, serif", textAlign: "left", cursor: isDisabled ? "default" : "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 16, height: 16, borderRadius: 4, border: isSelected ? "1px solid #C9A84C" : "1px solid #334155", background: isSelected ? "#C9A84C" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 10, color: "#060D16", fontWeight: 700 }}>
              {isSelected ? "✓" : ""}
            </span>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function MonthCard({ data, monthNum, accent }) {
  return (
    <div style={{ background: "#0D1B2A", border: `1px solid ${accent}33`, borderRadius: 12, overflow: "hidden", marginBottom: 20 }}>
      <div style={{ background: accent + "18", borderBottom: `1px solid ${accent}33`, padding: "18px 24px" }}>
        <div style={{ color: accent, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", fontFamily: "Georgia, serif", marginBottom: 4 }}>Month {monthNum}</div>
        <div style={{ color: "#fff", fontSize: 20, fontWeight: 700, fontFamily: "Georgia, serif", marginBottom: 4 }}>{data.theme}</div>
        <div style={{ color: "#94a3b8", fontSize: 13, fontFamily: "Georgia, serif", fontStyle: "italic" }}>{data.focus}</div>
      </div>
      <div style={{ padding: "18px 24px" }}>
        {data.weeks.map((w, i) => (
          <div key={i} style={{ display: "flex", gap: 14, marginBottom: 14, paddingBottom: 14, borderBottom: i < 3 ? "1px solid #1A2E45" : "none" }}>
            <div style={{ minWidth: 64, color: accent, fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", fontFamily: "Georgia, serif", paddingTop: 2 }}>{w.week}</div>
            <div>
              <div style={{ color: "#fff", fontSize: 14, fontWeight: 600, fontFamily: "Georgia, serif", marginBottom: 3 }}>{w.priority}</div>
              <div style={{ color: "#94a3b8", fontSize: 12, fontFamily: "Georgia, serif", lineHeight: 1.6 }}>{w.why}</div>
            </div>
          </div>
        ))}
        <div style={{ marginTop: 6, padding: "12px 16px", background: accent + "12", borderLeft: `3px solid ${accent}`, borderRadius: "0 8px 8px 0" }}>
          <div style={{ color: accent, fontSize: 9, letterSpacing: 2, textTransform: "uppercase", fontFamily: "Georgia, serif", marginBottom: 4 }}>Bill's Call</div>
          <div style={{ color: "#cbd5e1", fontSize: 12, fontFamily: "Georgia, serif", lineHeight: 1.7, fontStyle: "italic" }}>"{data.caskey_call}"</div>
        </div>
      </div>
    </div>
  );
}

function RoadmapView({ roadmap, answers, onRestart }) {
  const [emailStatus, setEmailStatus] = useState(null);
  const [sending, setSending] = useState(false);

  const handleSendEmail = async () => {
    setSending(true);
    setEmailStatus(null);
    try {
      const res = await fetch("/api/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "send_email", answers, roadmap }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setEmailStatus("success");
    } catch (e) {
      setEmailStatus("error: " + e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div style={{ textAlign: "center", marginBottom: 36, paddingBottom: 32, borderBottom: "1px solid #1A2E45" }}>
        <div style={{ color: "#C9A84C", fontSize: 10, letterSpacing: 4, textTransform: "uppercase", fontFamily: "Georgia, serif", marginBottom: 12 }}>Your Personalized</div>
        <div style={{ color: "#fff", fontSize: 22, fontWeight: 700, fontFamily: "Georgia, serif", lineHeight: 1.3, marginBottom: 16 }}>{roadmap.headline}</div>
        <div style={{ color: "#94a3b8", fontSize: 14, fontFamily: "Georgia, serif", lineHeight: 1.8, fontStyle: "italic" }}>{roadmap.opening}</div>
      </div>

      <div style={{ background: "linear-gradient(135deg, #C9A84C18, #C9A84C08)", border: "1px solid #C9A84C44", borderRadius: 10, padding: "20px 24px", marginBottom: 28, textAlign: "center" }}>
        <div style={{ color: "#C9A84C", fontSize: 10, letterSpacing: 3, textTransform: "uppercase", fontFamily: "Georgia, serif", marginBottom: 8 }}>Your 90-Day North Star</div>
        <div style={{ color: "#fff", fontSize: 16, fontFamily: "Georgia, serif", lineHeight: 1.6 }}>{roadmap.north_star}</div>
      </div>

      <MonthCard data={roadmap.month1} monthNum={1} accent="#C9A84C" />
      <MonthCard data={roadmap.month2} monthNum={2} accent="#7BAFD4" />
      <MonthCard data={roadmap.month3} monthNum={3} accent="#6BCB8B" />

      <div style={{ background: "#0D1B2A", border: "1px solid #C9A84C33", borderRadius: 12, padding: "28px 32px", marginBottom: 28 }}>
        <div style={{ color: "#C9A84C", fontSize: 10, letterSpacing: 3, textTransform: "uppercase", fontFamily: "Georgia, serif", marginBottom: 14 }}>From Bill</div>
        <div style={{ color: "#e2e8f0", fontSize: 15, fontFamily: "Georgia, serif", lineHeight: 1.9, fontStyle: "italic" }}>{roadmap.bill_closing}</div>
        <div style={{ marginTop: 16, color: "#C9A84C", fontSize: 12, fontFamily: "Georgia, serif" }}>— Bill Caskey | billcaskey.com</div>
      </div>

      <div style={{ background: "#0D1B2A", border: "1px solid #1A2E45", borderRadius: 12, padding: "24px 28px", marginBottom: 24, textAlign: "center" }}>
        <div style={{ color: "#fff", fontSize: 15, fontFamily: "Georgia, serif", marginBottom: 6 }}>Want a copy sent to your inbox?</div>
        <div style={{ color: "#64748b", fontSize: 12, fontFamily: "Georgia, serif", marginBottom: 20 }}>
          Click below — your branded PDF roadmap will be emailed to <strong style={{ color: "#94a3b8" }}>{answers.email}</strong>
        </div>
        {emailStatus === "success" ? (
          <div style={{ color: "#6BCB8B", fontSize: 14, fontFamily: "Georgia, serif", lineHeight: 1.7 }}>
            ✓ Sent! Your branded PDF roadmap is on its way to {answers.email}.
          </div>
        ) : emailStatus && emailStatus.startsWith("error") ? (
          <div>
            <div style={{ color: "#f87171", fontSize: 13, fontFamily: "Georgia, serif", marginBottom: 6 }}>Something went wrong. Please try again.</div>
            <div style={{ color: "#475569", fontSize: 10, fontFamily: "monospace" }}>{emailStatus}</div>
          </div>
        ) : (
          <button onClick={handleSendEmail} disabled={sending}
            style={{ background: sending ? "#1A2E45" : "linear-gradient(135deg, #C9A84C, #A8863C)", color: sending ? "#64748b" : "#060D16", border: "none", padding: "12px 32px", borderRadius: 8, fontSize: 13, fontWeight: 700, fontFamily: "Georgia, serif", letterSpacing: 0.5, cursor: sending ? "default" : "pointer", transition: "all 0.2s" }}>
            {sending ? "Sending..." : "📧 Email Me This Roadmap"}
          </button>
        )}
      </div>

      <div style={{ textAlign: "center" }}>
        <button onClick={onRestart} style={{ background: "transparent", border: "1px solid #1A2E45", color: "#64748b", padding: "9px 22px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontFamily: "Georgia, serif" }}>
          Start Over
        </button>
      </div>
    </div>
  );
}

export default function App() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [multiAnswers, setMultiAnswers] = useState([]);
  const [roadmap, setRoadmap] = useState(null);
  const [error, setError] = useState(null);

  const question = QUESTIONS[step - 1];
  const isMulti = question?.type === "multiselect";
  const canProceed = isMulti ? multiAnswers.length > 0 : currentAnswer.trim().length > 0;

  const handleNext = () => {
    if (!canProceed) return;
    const value = isMulti ? multiAnswers : currentAnswer;
    const newAnswers = { ...answers, [question.id]: value };
    setAnswers(newAnswers);
    setCurrentAnswer("");
    setMultiAnswers([]);
    if (step < QUESTIONS.length) {
      setStep(step + 1);
    } else {
      generateRoadmap(newAnswers);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      const prev = QUESTIONS[step - 2];
      if (prev.type === "multiselect") {
        setMultiAnswers(answers[prev.id] || []);
        setCurrentAnswer("");
      } else {
        setCurrentAnswer(answers[prev.id] || "");
        setMultiAnswers([]);
      }
      setStep(step - 1);
    }
  };

  const generateRoadmap = async (finalAnswers) => {
    setStep(100);
    setError(null);
    try {
      const response = await fetch("/api/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "generate",
          payload: {
            model: "claude-sonnet-4-20250514",
            max_tokens: 8000,
            messages: [{ role: "user", content: buildPrompt(finalAnswers) }],
          }
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const detail = errData?.error?.message || errData?.error || JSON.stringify(errData) || `HTTP ${response.status}`;
        setError(`API Error: ${detail}`);
        setStep(QUESTIONS.length);
        return;
      }

      const data = await response.json();

      if (!data.content || data.content.length === 0) {
        setError(`Empty response from API: ${JSON.stringify(data).slice(0, 200)}`);
        setStep(QUESTIONS.length);
        return;
      }

      const raw = data.content.map(b => b.text || "").join("");
      const clean = raw.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();

      let parsed;
      try {
        parsed = JSON.parse(clean);
      } catch(parseErr) {
        setError(`JSON parse error. Raw: ${clean.slice(0, 200)}`);
        setStep(QUESTIONS.length);
        return;
      }

      setRoadmap(parsed);
      setStep(101);
    } catch (e) {
      setError(`Network error: ${e.message}`);
      setStep(QUESTIONS.length);
    }
  };

  const handleRestart = () => {
    setStep(0); setAnswers({}); setCurrentAnswer("");
    setMultiAnswers([]); setRoadmap(null); setError(null);
  };

  const isQuestion = step >= 1 && step <= QUESTIONS.length;
  const isGenerating = step === 100;
  const isResult = step === 101;

  return (
    <div style={{ minHeight: "100vh", background: "#060D16", display: "flex", flexDirection: "column", alignItems: "center", padding: "32px 20px" }}>
      <style>{`* { box-sizing: border-box; } @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} } @keyframes slideUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }`}</style>

      <div style={{ marginBottom: 32, textAlign: "center" }}>
        <div style={{ color: "#C9A84C", fontSize: 10, letterSpacing: 5, textTransform: "uppercase", fontFamily: "Georgia, serif" }}>Bill Caskey</div>
        <div style={{ width: 32, height: 1, background: "#C9A84C44", margin: "8px auto" }} />
        <div style={{ color: "#1A3A5C", fontSize: 9, letterSpacing: 3, textTransform: "uppercase", fontFamily: "Georgia, serif" }}>2X Sprint</div>
      </div>

      <div style={{ width: "100%", maxWidth: 640, background: "#0D1B2A", borderRadius: 14, border: "1px solid #1A2E45", padding: "36px 40px", boxShadow: "0 40px 80px rgba(0,0,0,0.6)" }}>

        {step === 0 && (
          <div style={{ textAlign: "center", animation: "slideUp 0.6s ease" }}>
            <div style={{ width: 52, height: 52, background: "#C9A84C18", border: "1px solid #C9A84C44", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: 20 }}>🚀</div>
            <h1 style={{ color: "#fff", fontSize: 24, fontWeight: 700, fontFamily: "Georgia, serif", marginBottom: 14, lineHeight: 1.3 }}>Your 90-Day Revenue Roadmap</h1>
            <p style={{ color: "#94a3b8", fontSize: 14, fontFamily: "Georgia, serif", lineHeight: 1.8, marginBottom: 10 }}>
              Welcome to the 2X Sprint. Before we begin, I want to understand exactly where you are — so everything we build is designed for <em>you</em>, not a generic version of you.
            </p>
            <p style={{ color: "#94a3b8", fontSize: 14, fontFamily: "Georgia, serif", lineHeight: 1.8, marginBottom: 32 }}>
              11 quick questions. Then your personalized roadmap appears instantly.
            </p>
            <button onClick={() => setStep(1)} style={{ background: "linear-gradient(135deg, #C9A84C, #A8863C)", color: "#060D16", border: "none", padding: "13px 36px", borderRadius: 8, fontSize: 13, fontWeight: 700, fontFamily: "Georgia, serif", letterSpacing: 1, cursor: "pointer", textTransform: "uppercase" }}>
              Build My Roadmap →
            </button>
          </div>
        )}

        {isQuestion && (
          <div style={{ animation: "slideUp 0.4s ease" }} key={step}>
            <ProgressBar current={step} total={QUESTIONS.length} />
            <div style={{ color: "#fff", fontSize: 18, fontWeight: 600, fontFamily: "Georgia, serif", lineHeight: 1.4, marginBottom: 22 }}>{question.label}</div>

            {isMulti ? (
              <MultiSelect options={question.options} selected={multiAnswers} onChange={setMultiAnswers} max={question.max} />
            ) : question.type === "select" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 9, marginBottom: 28 }}>
                {question.options.map((opt) => (
                  <button key={opt} onClick={() => setCurrentAnswer(opt)}
                    style={{ background: currentAnswer === opt ? "#C9A84C18" : "#060D16", border: currentAnswer === opt ? "1px solid #C9A84C" : "1px solid #1A2E45", borderRadius: 8, padding: "11px 16px", color: currentAnswer === opt ? "#C9A84C" : "#94a3b8", fontSize: 13, fontFamily: "Georgia, serif", textAlign: "left", cursor: "pointer", transition: "all 0.2s" }}>
                    {opt}
                  </button>
                ))}
              </div>
            ) : (
              <textarea value={currentAnswer} onChange={(e) => setCurrentAnswer(e.target.value)}
                placeholder={question.placeholder} rows={3}
                style={{ width: "100%", background: "#060D16", border: "1px solid #1A2E45", borderRadius: 8, padding: "12px 14px", color: "#fff", fontSize: 13, fontFamily: "Georgia, serif", lineHeight: 1.6, resize: "none", marginBottom: 28, outline: "none" }} />
            )}

            {error && (
              <div style={{ color: "#f87171", fontSize: 12, fontFamily: "Georgia, serif", marginBottom: 16, padding: "10px 14px", background: "#1A0A0A", borderRadius: 6 }}>{error}</div>
            )}

            <div style={{ display: "flex", gap: 12, justifyContent: "space-between" }}>
              {step > 1 && (
                <button onClick={handleBack} style={{ background: "transparent", border: "1px solid #1A2E45", color: "#64748b", padding: "10px 20px", borderRadius: 7, fontSize: 12, fontFamily: "Georgia, serif", cursor: "pointer" }}>← Back</button>
              )}
              <button onClick={handleNext} disabled={!canProceed}
                style={{ marginLeft: "auto", background: canProceed ? "linear-gradient(135deg, #C9A84C, #A8863C)" : "#1A2E45", color: canProceed ? "#060D16" : "#334155", border: "none", padding: "10px 26px", borderRadius: 7, fontSize: 13, fontWeight: 700, fontFamily: "Georgia, serif", cursor: canProceed ? "pointer" : "default", transition: "all 0.2s" }}>
                {step === QUESTIONS.length ? "Generate My Roadmap →" : "Next →"}
              </button>
            </div>
          </div>
        )}

        {isGenerating && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: 32, marginBottom: 20, animation: "pulse 1.5s infinite" }}>⚡</div>
            <div style={{ color: "#C9A84C", fontSize: 13, fontFamily: "Georgia, serif", letterSpacing: 2, marginBottom: 10 }}>Building Your Roadmap</div>
            <div style={{ color: "#64748b", fontSize: 12, fontFamily: "Georgia, serif", lineHeight: 1.8 }}>Analyzing your answers and crafting<br />a plan built specifically for you...</div>
          </div>
        )}

        {isResult && roadmap && <RoadmapView roadmap={roadmap} answers={answers} onRestart={handleRestart} />}
      </div>

      <div style={{ marginTop: 24, color: "#1A2E45", fontSize: 10, letterSpacing: 2, fontFamily: "Georgia, serif", textTransform: "uppercase" }}>billcaskey.com · UPWARD.</div>
    </div>
  );
}
